"use client";
import { useState } from "react";
import cn from "@/lib/utils";
import { ChevronDown, ArrowUpRight, X, Menu } from "lucide-react";


export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    setActiveDropdown(null);
  };

  const toggleDropdown = (label: string) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  const navItems = [
    { label: "HOME", href: "/" },
    {
      label: "PORTFOLIO",
      href: "#",
      dropdown: [
        { label: "RESIDENTIAL", href: "/portfolio" },
        { label: "COMMERCIAL", href: "/portfolio?t=commercial" },
        { label: "INDUSTRIAL", href: "/portfolio?t=industrial" },
      ],
    },
    { label: "ABOUT US", href: "/about" },
    { label: "REVIEWS", href: "/reviews" },
  ];

  return (
    <header className="top-0 left-0 w-full z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border dark:border-secondary">
      <div className="max-w-7xl mx-auto px-3 md:px-6 lg:px-0">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="border backdrop-blur-3xl bg-background/80 lg:translate-y-8 flex gap-3 items-center lg:p-2 z-10">
            <img
              src="/images/logo.png"
              className="h-16 lg:h-24"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item, index) => (
              <div key={index} className="relative">
                {item.dropdown ? (
                  <button
                    className="flex items-center gap-1 text-foreground/80 hover:text-primary transition-colors"
                    onClick={() => toggleDropdown(item.label)}
                  >
                    <span>{item.label}</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                ) : (
                  <a
                    href={item.href}
                    className="text-foreground/80 hover:text-primary transition-colors"
                  >
                    <span>{item.label}</span>
                  </a>
                )}

                {item.dropdown && activeDropdown == item.label && (
                  <div className="z-50 absolute top-full left-0 block min-w-[200px] bg-background border rounded-md shadow-lg">
                    {item.dropdown.map((dropItem, dropIndex) => (
                      <a
                        href={dropItem.href}
                        key={dropIndex}
                        className="block px-4 py-2 text-sm text-foreground/80 hover:bg-primary hover:text-primary-foreground"
                      >
                        <span>{dropItem.label}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Book Now Button */}
          <a
            href="/contact"
            className="hidden lg:inline-flex rounded-md bg-primary px-6 py-2.5 text-primary-foreground hover:bg-primary/90 transition-colors items-center gap-2"
          >
            <span className="lg:text-[1.1rem] font-semibold">
              GET A FREE QUOTE
            </span>
            <ArrowUpRight className="h-5 w-5" />
          </a>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden relative z-10 p-2"
            aria-label={"Open menu"}
          >
            {isOpen ? (
              <X className="h-6 w-6 text-foreground" />
            ) : (
              <Menu className="h-6 w-6 text-foreground" />
            )}
          </button>
        </nav>

        {/* Mobile Menu */}
        <div
          className={cn(
            "fixed border shadow-xl w-full top-20 bg-background lg:hidden transition-transform duration-300 ease-in-out",
            isOpen ? "-translate-x-4" : "translate-x-full",
          )}
        >
          <nav className="h-full overflow-y-auto">
            <ul className="px-4 py-6 space-y-4">
              {navItems.map((item, index) => (
                <li key={index}>
                  {item.dropdown ? (
                    <div>
                      <button
                        onClick={() => toggleDropdown(item.label)}
                        className="flex items-center justify-between w-full text-lg text-foreground"
                      >
                        <span>{item.label}</span>
                        <ChevronDown
                          className={cn(
                            "h-5 w-5 transition-transform",
                            activeDropdown === item.label ? "rotate-180" : "",
                          )}
                        />
                      </button>
                      {activeDropdown === item.label && (
                        <div className="mt-2 ml-4 space-y-2 z-50">
                          {item.dropdown.map((dropItem, dropIndex) => (
                            <a
                              href={dropItem.href}
                              key={dropIndex}
                              className="block text-foreground/80 hover:text-primary"
                            >
                              <span>{dropItem.label}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <a
                      href={item.href}
                      className="block text-lg text-foreground hover:text-primary"
                    >
                      <span>{item.label}</span>
                    </a>
                  )}
                </li>
              ))}
              <li>
                <a
                  href="/contact"
                  className="inline-flex rounded-md bg-primary px-6 py-2.5 text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  GET A FREE QUOTE
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
}
